import axios, { AxiosResponse } from 'axios'
import { HttpService } from '@/services/common/HttpService'
import {
  Trip,
  VehicleAssignmentResult,
  OverridePaymentTermsPayload,
  Reservation,
  UpdatePurchaseOrderRequestDTO,
  InvoiceSentDateSidebarDetails,
  UpdateCancellationClassificationRequestDTO,
  FinalizeCancellationRequestDTO,
  ReservationContactsResponse,
  ReservationChatHistoryResponse,
  AssignableVehicleResult,
} from '@/models/dto'
import { TripMonitoringTableResult } from '@/models/dto/TripMonitoring'
import { PaymentTypeId } from '@/utils/enum'

const httpService: HttpService = new HttpService()

export const reservationsTableView = (query) =>
  httpService.get(`/tables/referrals?${query}`, {
    headers: { 'Cache-Control': 'private, no-cache, no-store, max-age=0' },
  })

export const reservationById = async (managedId: string): Promise<AxiosResponse<Reservation>> => {
  const url = `/reservations/${managedId}?viewRejectedReferrals=true`
  return httpService.get(url)
}

export const getAvailableDriversForRes = (reservationIds: number[], companyId: number) => {
  const url = `/tripAssignments/availableDrivers/${companyId}`
  return httpService.post(url, reservationIds)
}

export const getAssignableVehiclesForRes = (
  reservationId: number,
  companyId: number,
): Promise<AxiosResponse<AssignableVehicleResult>> => {
  const url = `/reservations/${reservationId}/companies/${companyId}/vehicles/assignment`
  return httpService.get(url)
}

export const getUnavailableDriversForRes = (reservationIds: number[], companyId: number) => {
  const url = `/tripAssignments/unavailableDrivers/${companyId}`

  return httpService.post(url, reservationIds)
}

export const getUnavailableVehiclesForRes = (reservationIds: number[], companyId: number) => {
  const url = `/tripAssignments/unavailableVehicles/${companyId}`
  return httpService.post(url, reservationIds)
}

export const validateTrip = (trip: Trip) => {
  const url = `/tripAssignments/validateTrip`
  return httpService.post(url, trip)
}

export const deleteTripAssignmentsWithIds = (assignmentIds: number[]) => {
  const url = `/tripAssignments/deleteAssignments`
  return httpService.delete(url, {
    data: { deleteAssignmentIds: assignmentIds },
  })
}

export const createTripAssignment = (payload) => {
  const url = `/tripAssignments/createAssignment`
  return httpService.post(url, payload)
}

export const deleteMassAssignment = (payload) => {
  const url = `/massAssignments/remove`
  return httpService.delete(url, { data: payload })
}

export const createMassAssignment = (payload) => {
  const url = `/massAssignments/create`
  return httpService.post(url, payload)
}

export const updateAssignments = (payload) => {
  const url = `/trips/validateTrip`
  return httpService.post(url, payload)
}

export const updateClassifications = (payload): Promise<AxiosResponse> => {
  const url = `/reservations/classifications`
  return httpService.patch(url, payload)
}

export const updateTier = (payload): Promise<AxiosResponse> => {
  const url = `/reservations/tier`
  return httpService.patch(url, payload)
}

export const updateSpotTimes = (reservationId: number, spotTimes) => {
  const url = `/reservations/${reservationId}/spotTimes`
  return httpService.patch(url, spotTimes)
}

export const detectVehicleConflict = (payload) => {
  const url = `/tripAssignments/detectVehicleConflict`
  return httpService.post(url, payload)
}

export const deleteSpotTime = (spotTime) => {
  const url = `/reservations/spotTime`
  return httpService.delete(url, spotTime)
}

export const getTripAssignmentsForReservation = async (payload): Promise<AxiosResponse<VehicleAssignmentResult>> => {
  const url = `/tripAssignments/reservation`

  const response: AxiosResponse<VehicleAssignmentResult> = await httpService.post(url, payload)
  // Rename simulated vehicles
  const tripsAssignmentsForReservation = response.data.vehicleAssignments
  for (const tripAssignment of tripsAssignmentsForReservation) {
    if (tripAssignment.vehicle.simulated) {
      tripAssignment.vehicle.vehicleModel = ''
      if (!tripAssignment.driverAssignments || tripAssignment.driverAssignments.length === 0) {
        tripAssignment.vehicle.vehicleName = 'Unassigned Vehicle '
      } else if (tripAssignment.driverAssignments.length === 1) {
        const driver = tripAssignment.driverAssignments[0].driver
        tripAssignment.vehicle.vehicleName = `Unassigned Vehicle (${driver.firstName})`
      } else {
        const driverNames = tripAssignment.driverAssignments
          .map((da) => da.driver.firstName.slice(0, 4) + '...')
          .join(', ')
        tripAssignment.vehicle.vehicleName = `Unassigned Vehicle (${driverNames})`
      }
    }
  }

  return response
}

export const getTrackingHistoryV2 = (payload, minutesBefore: number = 65, minutesAfter: number = 60) => {
  const url = `/v2/tracking/data?minutesBefore=${minutesBefore}&minutesAfter=${minutesAfter}`
  return httpService.post(url, payload)
}

export const finalizeCancellation = (payload: FinalizeCancellationRequestDTO) => {
  const url = `/cancellation/finalize`
  return httpService.post(url, payload)
}

export const setCancellationClassification = (payload: UpdateCancellationClassificationRequestDTO) => {
  const url = `/cancellation/classification`
  return httpService.patch(url, payload)
}

export const voidCancellation = (reservationId: number) => {
  const url = `/cancellation/void`
  return httpService.post(url, { reservationId })
}

export const getCancellationConfirmation = (
  reservationId: number,
): Promise<AxiosResponse<{ cancellationConfirmation: any }>> => {
  const url = `/reservations/${reservationId}/getCancellationConfirmationDetails`
  return httpService.get(url)
}

export const validateMassAssignmentEligibility = (payload) => {
  const url = `/massAssignments/validate`
  return httpService.post(url, payload)
}

export const getClassifications = (label: string): Promise<AxiosResponse> => {
  const url = `/classification/${label}`
  return httpService.get(url)
}

export const getRoutesForReservations = (
  reservationIds: number[],
  fetchChildren: boolean = false,
): Promise<AxiosResponse<any>> => {
  const url = `/reservations/tripVehicleGroups?fetchChildren=${fetchChildren}`
  return httpService.post(url, { reservationIds })
}

export const getReservationClassifications = async (reservationId: number) => {
  const response: AxiosResponse<any> = await httpService.get(`/reservations/${reservationId}/classifications`)
  if (response && response.data && response.data.successful === true) {
    return response.data.classifications
  }
  if (response && response.data && response.data.message) {
    throw new Error(response.data.message)
  } else {
    throw new Error('Unspecified error while getting reservation classifications.')
  }
}

export const createReservationClassification = async (reservationId: number, classificationId: number) => {
  const response: AxiosResponse<any> = await httpService.put(
    `/reservations/${reservationId}/classifications/${classificationId}`,
    null,
  )
  if (response && response.data && response.data.successful === true) {
    return response
  }
  if (response && response.data && response.data.message) {
    throw new Error(response.data.message)
  } else {
    throw new Error('Unspecified error while creating reservation classification.')
  }
}

export const shareReservation = async (reservationId: number, userId: number) => {
  const url = `/reservations/${reservationId}/share/${userId}`
  return httpService.post(url, {})
}

export const unshareReservation = async (reservationId: number, userId: number) => {
  const url = `/reservations/${reservationId}/unshare/${userId}`
  return httpService.post(url, {})
}

export const getReservationsForContractTracking = async (payload: {
  contractQuoteId: number
  date: string
  timeZone: string
  status: Array<string>
}) => {
  let query = ''
  const queryMap = new Map()
  if (payload.date) {
    queryMap.set('date', payload.date)
  }
  if (payload.timeZone) {
    queryMap.set('timeZone', payload.timeZone)
  }
  if (payload.status?.length) {
    queryMap.set('status', payload.status.join(','))
  }
  if (queryMap.size) {
    query = `?${Array.from(queryMap)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')}`
  }
  const url = `/coachrail/v1/contracts/${payload.contractQuoteId}/reservations${query}`
  return httpService.get(url)
}

export const getReservationDetailForContractTracking = async (contractQuoteId: number, reservationId: number) => {
  const url = `/coachrail/v1/contracts/${contractQuoteId}/reservations/${reservationId}`
  return httpService.get(url)
}

export const overridePaymentTerms = async (payload: OverridePaymentTermsPayload) => {
  const url = `/reservations/overridePaymentTerms`
  return httpService.post(url, payload)
}

export const recalculateAssignmentPercentages = async (reservationId: number) => {
  const url = `/reservations/${reservationId}/recalculateAssignmentPercentages`
  return httpService.post(url, {})
}

export const markTripModificationRequestComplete = async (managedId: string) => {
  const url = `/coachrail/v1/reservation/complete-trip-modification`
  return httpService.post(url, {
    managedId,
  })
}

export const getInvoiceSentDateSidebarDetails = async (
  reservationExternalId: string,
): Promise<AxiosResponse<InvoiceSentDateSidebarDetails>> => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/invoice-sent-date-sidebar`
  return httpService.get(url)
}

export const submitInvoiceSentDateSidebarDetails = async (
  reservationExternalId: string,
  sentDate: string,
  dueDate: string,
  invoiceNumber: string,
  paymentTermsDays: number,
) => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/invoice-sent-date-sidebar`
  return httpService.post(url, {
    sentDate,
    dueDate,
    invoiceNumber,
    paymentTermsDays,
  })
}

export const getReservationContacts = async (
  reservationExternalId: string,
): Promise<AxiosResponse<ReservationContactsResponse>> => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/contacts`
  return httpService.get(url)
}

export const addReservationContact = async (
  reservationExternalId: string,
  payload: any,
): Promise<AxiosResponse<ReservationContactsResponse>> => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/contacts`
  return httpService.post(url, payload)
}

export const editReservationContact = async (
  reservationExternalId: string,
  payload: any,
): Promise<AxiosResponse<ReservationContactsResponse>> => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/contacts/${payload.userExternalId}`
  return httpService.put(url, payload)
}

export const removeReservationContact = async (
  reservationExternalId: string,
  userId: string,
): Promise<AxiosResponse<ReservationContactsResponse>> => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/contacts/${userId}`
  return httpService.delete(url)
}

export const getReservationChatHistory = async (
  reservationId: number,
): Promise<AxiosResponse<ReservationChatHistoryResponse>> => {
  const url = `/coachrail/v1/reservation/${reservationId}/chat-history`
  return httpService.get(url)
}

export const getTripMonitoringReferrals = async (
  start: string,
  end: string,
): Promise<AxiosResponse<TripMonitoringTableResult>> => {
  const url = `/tripMonitoring/table?start=${start}&end=${end}`
  return httpService.get(url)
}

export const getTripMonitoringDetail = async (reservationId: number) => {
  const url = `/tripMonitoring/detail/${reservationId}`
  return httpService.get(url)
}

export const updateReservationPurchaseOrder = async (
  reservationExternalId: string,
  payload: UpdatePurchaseOrderRequestDTO,
) => {
  const url = `/reservations/${reservationExternalId}/purchaseOrder`
  return httpService.put(url, payload)
}

export const updateReservationPaymentType = async (reservationExternalId: string, paymentTypeId: PaymentTypeId) => {
  const url = `/reservations/${reservationExternalId}/paymentType/${paymentTypeId}`
  return httpService.put(url, null)
}

export const updateReservationCustomerServiceRepresentative = async (
  reservationExternalId: string,
  customerServiceRepresentativeExternalId: string,
) => {
  const url = `/coachrail/v1/reservation/${reservationExternalId}/customer-service-representative`
  return httpService.post(url, { customerServiceRepresentativeExternalId })
}
